import { useState, useCallback } from 'react';
import { useSyncStore } from '@/lib/store/sync.store';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { getStoredSessionId } from '@/lib/cloudflare/auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

interface TriggerSyncOptions {
  maxEmails?: number;
  onSuccess?: (jobId: string, totalEmails: number) => void;
  onError?: (error: Error) => void;
}

export function useTriggerSync() {
  const { user, accessToken } = useCFAuth();
  const { startSync, failSync, isPolling, progress } = useSyncStore();
  const [isStarting, setIsStarting] = useState(false);

  const isSyncing = isPolling && progress?.status === 'processing';

  const triggerSync = useCallback(async (options: TriggerSyncOptions = {}) => {
    const { maxEmails = 5000, onSuccess, onError } = options;

    if (!user || !accessToken) {
      const error = new Error('Not authenticated');
      onError?.(error);
      return { success: false, error };
    }

    if (isSyncing) {
      const error = new Error('Sync already in progress');
      onError?.(error);
      return { success: false, error };
    }

    setIsStarting(true);
    const sessionId = getStoredSessionId();

    try {
      const response = await fetch(`${CF_WORKER_URL}/api/sync-jobs/start`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
          accessToken,
          maxEmails,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Failed to start sync');
      }

      // Check if no emails were found
      if (!result.data.jobId) {
        console.log('No emails found to sync');
        setIsStarting(false);
        return { success: true, noEmails: true };
      }

      const { jobId, totalEmails, totalBatches } = result.data;
      console.log(`Sync started: ${totalEmails} emails in ${totalBatches} batches`);

      // Update store with job info - this triggers polling in authenticated-app
      startSync(jobId, totalEmails);
      setIsStarting(false);

      onSuccess?.(jobId, totalEmails);
      return { success: true, jobId, totalEmails };

    } catch (error) {
      console.error('Failed to start sync:', error);
      const err = error instanceof Error ? error : new Error('Failed to start sync');
      failSync(err.message);
      setIsStarting(false);
      onError?.(err);
      return { success: false, error: err };
    }
  }, [user, accessToken, isSyncing, startSync, failSync]);

  return {
    triggerSync,
    isStarting,
    isSyncing,
  };
}
